import { Box, Flex, Group, Modal, Paper, Select, Stack } from '@mantine/core';
import { ContactPoint, Organization } from '@medplum/fhirtypes';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { PartnerTypePill } from '../PartnerTypePill';
import { OrganizationType } from '@bariendo/lib/constants';
import { ORGANIZATION_TYPE_CODING_MAP } from '@bariendo/lib/codings';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { IconDeviceMobileMessage, IconNote, IconPhoneOutgoing } from '@tabler/icons-react';
import { ActionButtonGroup } from '@/components/Timeline/ActionButtonGroup';
import { getContactLabel, getPrimaryPhone } from '@/services/contact-point';
import { alert } from '@/services/alert';
import { MessageBoxType } from '@/components/Timeline/types';
import { getOrganizationPhonesStructured, createPhoneSections } from './services/timelineOrganization';
import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import { usePostHog } from 'posthog-js/react';
import { trackCallInitiated } from '@/services/analytics';
import { getReferenceString } from '@medplum/core';
import { EditableField } from '@/components/EditableField';
import { CreateFaxForm } from '@/pages/FaxingPage/CreateFaxForm';
import { OutboundIcon } from '@/components/icons/OutboundIcon';
import TimelineHeaderAddMenu from '@/components/Timeline/TimelineHeader/TimelineHeaderAddMenu';
import TimelineHeaderAddMenuItem from '@/components/Timeline/TimelineHeader/TimelineHeaderAddMenuItem';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';

function getOrganizationType(org?: Organization): OrganizationType {
  return (org?.type?.[0]?.coding?.[0]?.code as OrganizationType) || 'other';
}

type PartnerTimelineHeaderProps = {
  organization?: Organization;
};

export function PartnerTimelineHeader({ organization }: PartnerTimelineHeaderProps): ReactElement {
  const { partnerContext, messageBoxContext } = useTimelineContext();
  const { setMessageBoxVariant, setSelectedPhone, setShowMessageInput } = messageBoxContext;
  const updateOrganization = partnerContext?.updateOrganization;
  const { makeCall } = useTwilioPhone();
  const posthog = usePostHog();
  const [isFaxModalOpened, setIsFaxModalOpened] = useState(false);

  const [pickingOrgType, setPickingOrgType] = useState(false);
  const orgTypeSelectRef = useRef<HTMLInputElement>(null);

  const phonesData = useMemo(
    () => (organization ? getOrganizationPhonesStructured(organization) : null),
    [organization]
  );
  const primaryPhone = useMemo(() => (phonesData ? getPrimaryPhone(phonesData.allPhones) : undefined), [phonesData]);
  const smsPhones = useMemo(() => {
    if (!phonesData) return [];
    return phonesData.allPhones.filter((phone) => !!phone.value);
  }, [phonesData]);

  const handleNameChange = async (name: string) => {
    if (!organization || !updateOrganization) return;
    await updateOrganization({ ...organization, name });
  };

  const handleTypeChange = async (type: OrganizationType) => {
    if (!organization || !updateOrganization) return;
    await updateOrganization({
      ...organization,
      type: [{ coding: [ORGANIZATION_TYPE_CODING_MAP[type]] }],
    });
    setPickingOrgType(false);
  };

  const handleShowSmsMessageBox = useCallback(
    (phone?: ContactPoint) => {
      const selectedPhone = phone ?? primaryPhone;
      if (!selectedPhone) {
        alert.error("This partner doesn't have a phone number on file.");
        return;
      }

      setMessageBoxVariant(MessageBoxType.SMS);
      setSelectedPhone(selectedPhone);
      setShowMessageInput(true);
    },
    [primaryPhone, setMessageBoxVariant, setSelectedPhone, setShowMessageInput]
  );

  const smsPhoneItemsForAddMenu = useMemo(
    () =>
      smsPhones.map((phone) => ({
        key: phone.value!,
        label: getContactLabel(phone),
        caption: formatPhoneNumber(phone.value),
        onClick: () => handleShowSmsMessageBox(phone),
      })),
    [smsPhones, handleShowSmsMessageBox]
  );

  function handleShowNoteBox() {
    setMessageBoxVariant(MessageBoxType.STANDALONE_NOTE);
    setShowMessageInput(true);
  }

  const callPhoneSections = useMemo(() => {
    const handleCall = (phone: ContactPoint) => {
      if (organization?.id) {
        trackCallInitiated(posthog, {
          source: 'partner timeline header',
          organization_id: organization.id,
        });
      }
      const recipient = organization?.id ? getReferenceString(organization) : undefined;
      makeCall(phone.value!, undefined, recipient);
    };

    return createPhoneSections(phonesData, handleCall, handleCall);
  }, [phonesData, organization, posthog, makeCall]);

  return (
    <Paper p="md" withBorder radius="none">
      <Flex justify="space-between" align="center">
        <Group gap="md" align="center">
          <Stack gap={0}>
            <EditableField
              value={organization?.name || 'Untitled partner'}
              onSubmit={handleNameChange}
              editable={!!organization && !!updateOrganization}
              textProps={{ style: { fontSize: '16px', fontWeight: 600 } }}
            />
            {organization &&
              (!pickingOrgType ? (
                <Box
                  onClick={() => updateOrganization && setPickingOrgType(true)}
                  style={{ cursor: updateOrganization ? 'pointer' : 'default' }}
                >
                  <PartnerTypePill type={getOrganizationType(organization)} />
                </Box>
              ) : (
                <Select
                  size="xs"
                  ref={orgTypeSelectRef}
                  allowDeselect={false}
                  placeholder="Choose type"
                  data={Object.values(OrganizationType).map((type) => ({
                    value: type,
                    label: ORGANIZATION_TYPE_CODING_MAP[type]?.display || type,
                  }))}
                  defaultValue={getOrganizationType(organization)}
                  onChange={(value) => value && handleTypeChange(value as OrganizationType)}
                  onBlur={() => setPickingOrgType(false)}
                />
              ))}
          </Stack>
        </Group>

        <Group gap="xs">
          <TimelineHeaderAddMenu>
            <TimelineHeaderAddMenuItem
              icon={<IconNote size="14px" />}
              label="Sticky note"
              onClick={handleShowNoteBox}
            />
            <TimelineHeaderAddMenuItem
              icon={<IconDeviceMobileMessage size="14px" />}
              label="SMS message"
              dropdownItems={smsPhoneItemsForAddMenu}
              onClick={() => handleShowSmsMessageBox()}
            />
            <TimelineHeaderAddMenuItem
              icon={<OutboundIcon size={14} theme="dark" />}
              label="Fax"
              onClick={() => setIsFaxModalOpened(true)}
            />
          </TimelineHeaderAddMenu>
          <ActionButtonGroup
            primaryButton={{
              icon: <IconPhoneOutgoing size={16} />,
              onClick: () => {
                // Track call initiated from partner timeline header
                if (organization?.id) {
                  trackCallInitiated(posthog, {
                    source: 'partner timeline header',
                    organization_id: organization.id,
                  });
                }
                const phoneNumber = primaryPhone?.value ?? phonesData?.allPhones[0]?.value ?? '';
                const recipient = organization?.id ? getReferenceString(organization) : undefined;
                makeCall(phoneNumber, undefined, recipient);
              },
              tooltip: 'Call partner',
            }}
            dropdownSections={callPhoneSections}
            dropdownTooltip="Select a phone"
          />
        </Group>
      </Flex>
      <Modal
        opened={isFaxModalOpened}
        onClose={() => setIsFaxModalOpened(false)}
        title="Outgoing fax"
        centered
        size="xl"
      >
        <CreateFaxForm onClose={() => setIsFaxModalOpened(false)} initialOrganization={organization} />
      </Modal>
    </Paper>
  );
}
